import { type ReactNode, useCallback, useMemo } from 'react'

import { type Interceptor } from '@connectrpc/connect'
import { TransportProvider } from '@connectrpc/connect-query'
import { createConnectTransport } from '@connectrpc/connect-web'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { useAuth } from '@enechain/auth-js'

import { buildPreviewUrl } from '~/libs/preview'

import { queryRetryDelay, shouldRetryQuery } from './queryRetryPolicy'

/**
 * アプリ全体で使う QueryClient を生成する production の唯一の経路。
 * `ApiClientProvider.test.ts` はこの factory を通して実インスタンスの retry 配線
 * (retry / retryDelay が policy へ接続されていること) を検証する (issue #7070)。
 * component test の harness (`renderWithProviders`) はこの factory を通らず別途
 * `retry: false` を使う (意図的な divergence。`src/tests/README.md` 参照)。
 * mutation は変更しない (D3・R3: 二重発注防止のため retry しない。
 * defaultOptions.mutations は未設定 = react-query 既定 retry 0)。
 */
export function createAppQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        throwOnError: true,
        refetchOnWindowFocus: false,
        // 一過性エラー (Unavailable / DeadlineExceeded / 実通信断) のみ有限回
        // リトライして瞬断を吸収する。業務エラーは即確定する (issue #7070)。
        retry: shouldRetryQuery,
        retryDelay: queryRetryDelay,
      },
    },
  })
}

const queryClient = createAppQueryClient()

type Props = {
  children: ReactNode
}

export const ApiClientProvider = ({ children }: Props): React.ReactElement => {
  const { getToken } = useAuth()

  const authInterceptor: Interceptor = useCallback(
    (next) => async (req) => {
      const token = await getToken()
      req.header.set('Authorization', `Bearer ${token}`)
      req.header.set('X-TraceId', crypto.randomUUID())
      return next(req)
    },
    [getToken],
  )

  const transport = useMemo(
    () =>
      createConnectTransport({
        baseUrl: buildPreviewUrl(import.meta.env.VITE_API_URL),
        interceptors: [authInterceptor],
      }),
    [authInterceptor],
  )

  return (
    <TransportProvider transport={transport}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </TransportProvider>
  )
}
